import axios from 'utils/axios';

// Fetches throw (the store catches); writes return the response or the error
// body so callers can branch on statusCode / message.
function rethrow(e) {
    if (e && e.statusCode && e.message) {
        throw e;
    }
    throw new Error('unknown error');
}

function asResult(e) {
    if (e && e.statusCode && e.message) {
        return e;
    }
    return { message: 'unknown error' };
}

const PLAN_BASE = '/api/org-id/{orgId}/productivity-plan';

// Whole org roster × every day of `month` (YYYY-MM), derived goals and actuals. Omit the
// month for the server's current month in the org zone. Productivity Management or ORG_ADMIN.
export async function fetchOrgMonthGoalProgress(month) {
    try {
        return await axios.get(`/api/org-id/{orgId}/productivity-goal-day/org-month`, { params: { month } });
    } catch (e) {
        return rethrow(e);
    }
}

// --- plans ---------------------------------------------------------------

// Every active roster user with the plan row in force today, prior-row count
// and upcoming row.
export async function fetchCurrentPlans() {
    try {
        return await axios.get(`${PLAN_BASE}/current`);
    } catch (e) {
        return rethrow(e);
    }
}

export async function fetchPlanHistory(userId) {
    try {
        return await axios.get(`${PLAN_BASE}/user/${userId}/history`);
    } catch (e) {
        return rethrow(e);
    }
}

// plan: { effectiveFrom: 'YYYY-MM-DD', worksMon..worksSun, hoursPerDay, dailyDpc }
// Upserts on (userId, effectiveFrom): same date replaces in place, new date starts a new row.
export async function putPlan(userId, plan) {
    try {
        return await axios.put(`${PLAN_BASE}/user/${userId}`, plan);
    } catch (e) {
        return asResult(e);
    }
}

export async function deletePlan(planId) {
    try {
        return await axios.delete(`${PLAN_BASE}/${planId}`);
    } catch (e) {
        return asResult(e);
    }
}

// --- adjustments ---------------------------------------------------------

// filters: { month?: 'YYYY-MM', userId?, type? }
export async function fetchAdjustments(filters) {
    try {
        return await axios.get(`${PLAN_BASE}/adjustments`, { params: filters });
    } catch (e) {
        return rethrow(e);
    }
}

// adjustment: { userId, type, startDate, endDate, hoursPerDay, reason|null, notes|null }; both dates inclusive
export async function postAdjustment(adjustment) {
    try {
        return await axios.post(`${PLAN_BASE}/adjustments`, adjustment);
    } catch (e) {
        return asResult(e);
    }
}

export async function putAdjustment(adjustmentId, adjustment) {
    try {
        return await axios.put(`${PLAN_BASE}/adjustments/${adjustmentId}`, adjustment);
    } catch (e) {
        return asResult(e);
    }
}

export async function deleteAdjustment(adjustmentId) {
    try {
        return await axios.delete(`${PLAN_BASE}/adjustments/${adjustmentId}`);
    } catch (e) {
        return asResult(e);
    }
}

// --- closures ------------------------------------------------------------

export async function fetchClosures(year) {
    try {
        return await axios.get(`${PLAN_BASE}/closures`, { params: { year } });
    } catch (e) {
        return rethrow(e);
    }
}

export async function putClosure(date, label) {
    try {
        return await axios.put(`${PLAN_BASE}/closures/${date}`, { label });
    } catch (e) {
        return asResult(e);
    }
}

export async function deleteClosure(date) {
    try {
        return await axios.delete(`${PLAN_BASE}/closures/${date}`);
    } catch (e) {
        return asResult(e);
    }
}

// --- briefings -----------------------------------------------------------

const BRIEFING_BASE = '/api/org-id/{orgId}/productivity-briefing';

// Every briefing covering `month` (YYYY-MM), newest first. Omit the month for
// the server's current month. Productivity Management or ORG_ADMIN.
export async function fetchBriefings(month) {
    try {
        return await axios.get(BRIEFING_BASE, { params: { month } });
    } catch (e) {
        return rethrow(e);
    }
}

// Generates and stores a briefing for one user now (the server calls the
// model inline, so this takes a few seconds). 409 with a message when there
// is nothing to brief yet.
export async function sendBriefing(userId) {
    try {
        return await axios.post(`${BRIEFING_BASE}/user/${userId}`);
    } catch (e) {
        return asResult(e);
    }
}
